import { ContextMenuAction } from '../../explorer/models/TreeModel';
import type { IndexNodeMetadata } from '../../explorer/models/nodeTypes';
import { getConnectionById } from '../../connections/utils/connectionLookup';
import { toast } from '../../../components/ui/toaster';
import { writeClipboardText } from '../../../lib/clipboard';
import { safeConnectionAlias, safeConnectionServer } from '../../../types/safeConnectionDisplay';

/**
 * Index-specific context menu action types
 */
type IndexContextMenuActionType =
  | 'copy-name'
  | 'script-index-as'
  | 'script-index-create'
  | 'script-index-drop'
  | 'script-index-rebuild';

const IndexActions = {
  COPY_NAME: 'copy-name' as IndexContextMenuActionType,
  SCRIPT_INDEX_AS: 'script-index-as' as IndexContextMenuActionType,
  SCRIPT_INDEX_CREATE: 'script-index-create' as IndexContextMenuActionType,
  SCRIPT_INDEX_DROP: 'script-index-drop' as IndexContextMenuActionType,
  SCRIPT_INDEX_REBUILD: 'script-index-rebuild' as IndexContextMenuActionType,
};

/**
 * Generate context menu actions for an index node
 */
export function generateIndexContextMenuActions(): ContextMenuAction[] {
  return [
    { id: IndexActions.COPY_NAME, label: 'Copy index name', icon: 'clipboard' },
    {
      id: IndexActions.SCRIPT_INDEX_AS,
      label: 'Script as',
      icon: 'code',
      submenu: [
        { id: IndexActions.SCRIPT_INDEX_CREATE, label: 'CREATE', icon: 'file-plus' },
        { id: IndexActions.SCRIPT_INDEX_DROP, label: 'DROP', icon: 'trash' },
        { id: IndexActions.SCRIPT_INDEX_REBUILD, label: 'REBUILD', icon: 'refresh' },
      ],
    },
  ];
}

/**
 * Generate CREATE INDEX DDL (engine-aware)
 */
export function generateIndexCreateDdl(
  metadata: IndexNodeMetadata,
  engine: string,
  quoteId: (name: string) => string,
  qualifyTable: (schema: string, table: string) => string,
): string {
  const fullTable = qualifyTable(metadata.schema, metadata.tableName);
  const cols = metadata.columns.map(c => quoteId(c)).join(', ');
  const unique = metadata.isUnique ? 'UNIQUE ' : '';

  if (engine === 'pgsql') {
    // PostgreSQL: CREATE [UNIQUE] INDEX name ON table USING method (cols)
    const using = metadata.indexType ? ` USING ${metadata.indexType.toLowerCase()}` : '';
    return `CREATE ${unique}INDEX ${quoteId(metadata.indexName)}\n    ON ${fullTable}${using} (${cols});\n`;
  }

  if (engine === 'mysql') {
    // MySQL: CREATE [UNIQUE] INDEX name ON table (cols) — type is inferred
    return `CREATE ${unique}INDEX ${quoteId(metadata.indexName)}\n    ON ${fullTable} (${cols});\n`;
  }

  // MSSQL: CREATE [UNIQUE] [NONCLUSTERED] INDEX name ON table (cols)
  const indexType = metadata.indexType ? `${metadata.indexType} ` : '';
  return `CREATE ${unique}${indexType}INDEX ${quoteId(metadata.indexName)}\n    ON ${fullTable} (${cols});\n`;
}

/**
 * Generate DROP INDEX DDL (engine-aware)
 */
export function generateIndexDropDdl(
  metadata: IndexNodeMetadata,
  engine: string,
  quoteId: (name: string) => string,
  qualifyTable: (schema: string, table: string) => string,
): string {
  if (engine === 'mssql') {
    const fullTable = qualifyTable(metadata.schema, metadata.tableName);
    return `DROP INDEX ${quoteId(metadata.indexName)} ON ${fullTable};\n`;
  }

  if (engine === 'mysql') {
    const fullTable = qualifyTable(metadata.schema, metadata.tableName);
    return `DROP INDEX ${quoteId(metadata.indexName)} ON ${fullTable};\n`;
  }

  if (engine === 'pgsql') {
    // PostgreSQL — schema-qualify index name for non-search_path schemas
    const qualifiedIndex = qualifyTable(metadata.schema, metadata.indexName);
    return `DROP INDEX ${qualifiedIndex};\n`;
  }

  // SQLite — index name is sufficient
  return `DROP INDEX ${quoteId(metadata.indexName)};\n`;
}

/**
 * Generate REBUILD INDEX DDL (engine-aware)
 */
export function generateIndexRebuildDdl(
  metadata: IndexNodeMetadata,
  engine: string,
  quoteId: (name: string) => string,
  qualifyTable: (schema: string, table: string) => string,
): string {
  if (engine === 'mssql') {
    const fullTable = qualifyTable(metadata.schema, metadata.tableName);
    return `ALTER INDEX ${quoteId(metadata.indexName)} ON ${fullTable} REBUILD;\n`;
  }

  if (engine === 'pgsql') {
    const qualifiedIndex = qualifyTable(metadata.schema, metadata.indexName);
    return `REINDEX INDEX ${qualifiedIndex};\n`;
  }

  if (engine === 'mysql') {
    const fullTable = qualifyTable(metadata.schema, metadata.tableName);
    const cols = metadata.columns.map(c => quoteId(c)).join(', ');
    const unique = metadata.isUnique ? 'UNIQUE ' : '';
    return `ALTER TABLE ${fullTable} DROP INDEX ${quoteId(metadata.indexName)};\nALTER TABLE ${fullTable} ADD ${unique}INDEX ${quoteId(metadata.indexName)} (${cols});\n`;
  }

  // SQLite — REINDEX
  return `REINDEX ${quoteId(metadata.indexName)};\n`;
}

/**
 * Execute an index context menu action
 */
export function executeIndexContextMenuAction(
  actionId: string,
  metadata: IndexNodeMetadata,
): void {
  if (actionId === IndexActions.COPY_NAME) {
    void writeClipboardText(metadata.indexName);
    return;
  }

  if (
    actionId === IndexActions.SCRIPT_INDEX_CREATE ||
    actionId === IndexActions.SCRIPT_INDEX_DROP ||
    actionId === IndexActions.SCRIPT_INDEX_REBUILD
  ) {
    void handleIndexScript(metadata, actionId);
  }
}

/**
 * Generate index script and open in a new query tab
 */
async function handleIndexScript(
  metadata: IndexNodeMetadata,
  actionId: string,
): Promise<void> {
  const { getScriptProvider } = await import('../../scripting');
  const { useWorkspaceStore } = await import('../../workspace/core/store/workspaceStore');

  const { activeConnection, connection, engine } = getConnectionById(metadata.connectionId);

  if (!activeConnection || !connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const provider = getScriptProvider(engine);
  const quoteId = (name: string) => provider.quoteIdentifier(name);
  const qualifyTable = (schema: string, table: string) => provider.qualifyTableName(schema, table);

  let script: string;
  let titleVerb: string;

  if (actionId === IndexActions.SCRIPT_INDEX_CREATE) {
    script = generateIndexCreateDdl(metadata, engine, quoteId, qualifyTable);
    titleVerb = 'CREATE';
  } else if (actionId === IndexActions.SCRIPT_INDEX_DROP) {
    script = generateIndexDropDdl(metadata, engine, quoteId, qualifyTable);
    titleVerb = 'DROP';
  } else {
    script = generateIndexRebuildDdl(metadata, engine, quoteId, qualifyTable);
    titleVerb = 'REBUILD';
  }

  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();

  addTab({
    title: `${metadata.indexName}_${titleVerb}-${queryNumber}`,
    type: 'query',
    content: { query: script },
    connectionInfo: {
      connectionId: metadata.connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database: metadata.database,
    },
  });
}
